import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';

// ── Types ─────────────────────────────────────────────────────

interface MembershipLevel {
  id: string;
  name: string;
  description: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

type SortColumn = 'name' | 'description' | 'sortOrder';
type SortDirection = 'asc' | 'desc';

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginBottom: '1.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  title: {
    color: '#0f2d1f',
    fontSize: '1.1rem',
    margin: 0,
    borderBottom: '2px solid #c9a84c',
    paddingBottom: '0.25rem',
  },
  btn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnSmall: {
    padding: '0.3rem 0.6rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginRight: '0.25rem',
  },
  btnDanger: {
    padding: '0.3rem 0.6rem',
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnSecondary: {
    padding: '0.3rem 0.6rem',
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginRight: '0.25rem',
  },
  btnGold: {
    padding: '0.5rem 1rem',
    backgroundColor: '#c9a84c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.6rem 0.5rem',
    borderBottom: '2px solid #e0e0e0',
    fontWeight: 600,
    color: '#0f2d1f',
    fontSize: '0.8rem',
    cursor: 'pointer',
    userSelect: 'none' as const,
  },
  thActions: {
    textAlign: 'right' as const,
    padding: '0.6rem 0.5rem',
    borderBottom: '2px solid #e0e0e0',
    fontWeight: 600,
    color: '#0f2d1f',
    fontSize: '0.8rem',
  },
  td: {
    padding: '0.6rem 0.5rem',
    borderBottom: '1px solid #e0e0e0',
    fontSize: '0.85rem',
    color: '#333',
  },
  tdActions: {
    padding: '0.6rem 0.5rem',
    borderBottom: '1px solid #e0e0e0',
    textAlign: 'right' as const,
  },
  input: {
    padding: '0.4rem 0.6rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.85rem',
    boxSizing: 'border-box' as const,
    width: '100%',
  },
  inputSmall: {
    padding: '0.4rem 0.6rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.85rem',
    boxSizing: 'border-box' as const,
    width: '70px',
  },
  addForm: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    marginTop: '0.75rem',
    padding: '0.75rem',
    backgroundColor: '#f9fdf9',
    border: '1px solid #c9e8d4',
    borderRadius: '4px',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '0.75rem',
    fontSize: '0.85rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '1.5rem',
    color: '#666',
    fontSize: '0.9rem',
  },
  empty: {
    textAlign: 'center' as const,
    padding: '1.5rem',
    color: '#666',
    fontSize: '0.9rem',
  },
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  dialog: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '1.5rem',
    maxWidth: '400px',
    width: '90%',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  },
  dialogTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#0f2d1f',
    marginBottom: '0.75rem',
  },
  dialogText: {
    fontSize: '0.9rem',
    color: '#333',
    marginBottom: '1.25rem',
    lineHeight: 1.5,
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
  },
};

// ── Component ─────────────────────────────────────────────────

export function MembershipLevelsManager() {
  const [levels, setLevels] = useState<MembershipLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Add form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addName, setAddName] = useState('');
  const [addDescription, setAddDescription] = useState('');
  const [addSortOrder, setAddSortOrder] = useState('0');
  const [submitting, setSubmitting] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSortOrder, setEditSortOrder] = useState('');

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<{
    id: string;
    name: string;
    memberCount?: number;
  } | null>(null);

  // Sort state
  const [sortColumn, setSortColumn] = useState<SortColumn>('sortOrder');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // ── Data Fetching ─────────────────────────────────────────

  useEffect(() => {
    fetchLevels();
  }, []);

  async function fetchLevels() {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient<MembershipLevel[]>('/membership-levels');
      setLevels(data);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load membership levels.');
    } finally {
      setLoading(false);
    }
  }

  // ── Sorting ───────────────────────────────────────────────

  function handleSort(column: SortColumn) {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }

  function getSortedLevels(): MembershipLevel[] {
    return [...levels].sort((a, b) => {
      let comparison = 0;
      if (sortColumn === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (sortColumn === 'description') {
        comparison = a.description.localeCompare(b.description);
      } else if (sortColumn === 'sortOrder') {
        comparison = a.sortOrder - b.sortOrder;
        if (comparison === 0) {
          comparison = a.name.localeCompare(b.name);
        }
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  function getSortIndicator(column: SortColumn): string {
    if (sortColumn !== column) return '';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  }

  // ── Add ───────────────────────────────────────────────────

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!addName.trim()) return;

    setSubmitting(true);
    setError('');
    try {
      await apiClient('/membership-levels', {
        method: 'POST',
        body: {
          name: addName.trim(),
          description: addDescription.trim(),
          sortOrder: parseInt(addSortOrder, 10) || 0,
        },
      });
      setAddName('');
      setAddDescription('');
      setAddSortOrder('0');
      setShowAddForm(false);
      await fetchLevels();
    } catch (err: unknown) {
      const apiErr = err as { message?: string; statusCode?: number };
      if (apiErr.statusCode === 409) {
        setError('A membership level with this name already exists.');
      } else {
        setError(apiErr.message || 'Failed to create membership level.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── Edit ──────────────────────────────────────────────────

  function startEdit(level: MembershipLevel) {
    setEditingId(level.id);
    setEditName(level.name);
    setEditDescription(level.description);
    setEditSortOrder(String(level.sortOrder));
    setError('');
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName('');
    setEditDescription('');
    setEditSortOrder('');
  }

  async function handleSaveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editingId || !editName.trim()) return;

    setSubmitting(true);
    setError('');
    try {
      await apiClient(`/membership-levels/${editingId}`, {
        method: 'PATCH',
        body: {
          name: editName.trim(),
          description: editDescription.trim(),
          sortOrder: parseInt(editSortOrder, 10) || 0,
        },
      });
      setEditingId(null);
      await fetchLevels();
    } catch (err: unknown) {
      const apiErr = err as { message?: string; statusCode?: number };
      if (apiErr.statusCode === 409) {
        setError('A membership level with this name already exists.');
      } else {
        setError(apiErr.message || 'Failed to update membership level.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── Delete ────────────────────────────────────────────────

  async function handleDeleteClick(level: MembershipLevel) {
    setError('');
    setSubmitting(true);
    try {
      await apiClient(`/membership-levels/${level.id}`, { method: 'DELETE' });
      // Deletion succeeded — no members affected
      await fetchLevels();
    } catch (err: unknown) {
      const apiErr = err as { message?: string; statusCode?: number };
      if (apiErr.statusCode === 409) {
        // Extract member count from the error message
        const match = apiErr.message?.match(/(\d+)\s+member/);
        const memberCount = match ? parseInt(match[1], 10) : undefined;
        setDeleteConfirm({ id: level.id, name: level.name, memberCount });
      } else {
        setError(apiErr.message || 'Failed to delete membership level.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  function cancelDelete() {
    setDeleteConfirm(null);
  }

  // ── Render ────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={styles.container}>
        <h3 style={styles.title}>Membership Levels</h3>
        <div style={styles.loading}>Loading membership levels...</div>
      </div>
    );
  }

  const sortedLevels = getSortedLevels();

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>Membership Levels</h3>
        {!showAddForm && (
          <button
            style={styles.btn}
            onClick={() => setShowAddForm(true)}
            disabled={submitting}
          >
            + Add Level
          </button>
        )}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Add Form */}
      {showAddForm && (
        <form style={styles.addForm} onSubmit={handleAdd}>
          <input
            type="text"
            style={styles.input}
            placeholder="Level name"
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
            maxLength={100}
            required
            aria-label="Level name"
          />
          <input
            type="text"
            style={styles.input}
            placeholder="Description (optional)"
            value={addDescription}
            onChange={(e) => setAddDescription(e.target.value)}
            maxLength={500}
            aria-label="Level description"
          />
          <input
            type="number"
            style={styles.inputSmall}
            placeholder="Order"
            value={addSortOrder}
            onChange={(e) => setAddSortOrder(e.target.value)}
            aria-label="Sort order"
          />
          <button type="submit" style={styles.btnSmall} disabled={submitting}>
            Save
          </button>
          <button
            type="button"
            style={styles.btnSecondary}
            onClick={() => {
              setShowAddForm(false);
              setAddName('');
              setAddDescription('');
              setAddSortOrder('0');
            }}
            disabled={submitting}
          >
            Cancel
          </button>
        </form>
      )}

      {/* Table */}
      {sortedLevels.length === 0 ? (
        <div style={styles.empty}>
          No membership levels configured. Click "+ Add Level" to create one.
        </div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th} onClick={() => handleSort('name')}>
                Name{getSortIndicator('name')}
              </th>
              <th style={styles.th} onClick={() => handleSort('description')}>
                Description{getSortIndicator('description')}
              </th>
              <th style={styles.th} onClick={() => handleSort('sortOrder')}>
                Sort Order{getSortIndicator('sortOrder')}
              </th>
              <th style={styles.thActions}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedLevels.map((level) =>
              editingId === level.id ? (
                <tr key={level.id}>
                  <td style={styles.td}>
                    <input
                      type="text"
                      style={styles.input}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      maxLength={100}
                      required
                      aria-label="Edit level name"
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="text"
                      style={styles.input}
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      maxLength={500}
                      aria-label="Edit level description"
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="number"
                      style={styles.inputSmall}
                      value={editSortOrder}
                      onChange={(e) => setEditSortOrder(e.target.value)}
                      aria-label="Edit sort order"
                    />
                  </td>
                  <td style={styles.tdActions}>
                    <button
                      style={styles.btnSmall}
                      onClick={handleSaveEdit}
                      disabled={submitting}
                    >
                      Save
                    </button>
                    <button
                      style={styles.btnSecondary}
                      onClick={cancelEdit}
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                  </td>
                </tr>
              ) : (
                <tr key={level.id}>
                  <td style={styles.td}>{level.name}</td>
                  <td style={styles.td}>{level.description || '—'}</td>
                  <td style={styles.td}>{level.sortOrder}</td>
                  <td style={styles.tdActions}>
                    <button
                      style={styles.btnSmall}
                      onClick={() => startEdit(level)}
                      disabled={submitting}
                    >
                      Edit
                    </button>
                    <button
                      style={styles.btnDanger}
                      onClick={() => handleDeleteClick(level)}
                      disabled={submitting}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div style={styles.overlay} onClick={cancelDelete}>
          <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div style={styles.dialogTitle}>Cannot Delete Membership Level</div>
            <div style={styles.dialogText}>
              The membership level "<strong>{deleteConfirm.name}</strong>" is
              currently assigned to{' '}
              <strong>
                {deleteConfirm.memberCount ?? 'one or more'} member
                {deleteConfirm.memberCount !== 1 ? 's' : ''}
              </strong>
              . Please reassign those members before deleting this level.
            </div>
            <div style={styles.dialogActions}>
              <button style={styles.btnSecondary} onClick={cancelDelete}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
